import type { DealInsightsGeneralStatsApi } from '@openx/types/dealInsights.ts';
import type { TFunction } from 'i18next';

export type StatsPanelConfig = {
  key: keyof Omit<DealInsightsGeneralStatsApi, 'previous_period_calculated'>;
  hide: boolean;
};

export type StatsPanelLabelConfig = {
  label: string;
  prefix?: string;
  suffix?: string;
  roundToInteger?: boolean;
};

export type StatsPanelItem = {
  key: keyof Omit<DealInsightsGeneralStatsApi, 'previous_period_calculated'>;
  label: string;
  value: number | null | undefined;
  prefix?: string;
  suffix?: string;
  multiplier?: number;
  change: number;
  changeLabel: string;
  hide: boolean;
};

export type StatsPanelSettings = {
  impressions: boolean;
  spend: boolean;
  ecpm: boolean;
  bid_rate: boolean;
  win_rate: boolean;
  viewability: boolean;
  vcr: boolean;
  ctr: boolean;
  iqs_on_impression: boolean;
};

export type GeneralStatsPanelKey = keyof Omit<DealInsightsGeneralStatsApi, 'previous_period_calculated'>;

export const INVENTORY_QUALITY_PANEL_KEY = 'iqs_on_impression' as const;

export const BASE_STATS_PANEL_ORDER: GeneralStatsPanelKey[] = [
  'impressions',
  'spend',
  'ecpm',
  'bid_rate',
  'win_rate',
  'viewability',
  'vcr',
  'ctr',
];

export const DEFAULT_STATS_PANEL_ORDER: GeneralStatsPanelKey[] = [
  ...BASE_STATS_PANEL_ORDER,
  INVENTORY_QUALITY_PANEL_KEY,
];

export const GENERAL_STATS_PANEL_KEYS = new Set<GeneralStatsPanelKey>([
  ...BASE_STATS_PANEL_ORDER,
  INVENTORY_QUALITY_PANEL_KEY,
]);

export const getStatsPanelLabel = (key: keyof DealInsightsGeneralStatsApi, t: TFunction): StatsPanelLabelConfig => {
  switch (key) {
    case 'impressions':
      return {
        label: t('Impressions'),
      };
    case 'spend':
      return {
        label: t('Spend'),
        prefix: '$',
      };
    case 'ecpm':
      return {
        label: t('eCPM'),
        prefix: '$',
      };
    case 'bid_rate':
      return {
        label: t('Bid Rate'),
        suffix: '%',
      };
    case 'win_rate':
      return {
        label: t('Win Rate'),
        suffix: '%',
      };
    case 'viewability':
      return {
        label: t('Viewability'),
        suffix: '%',
      };
    case 'vcr':
      return {
        label: t('VCR'),
        suffix: '%',
      };
    case 'ctr':
      return {
        label: t('CTR'),
        suffix: '%',
      };
    case INVENTORY_QUALITY_PANEL_KEY:
      return {
        label: t('Inventory Quality'),
        roundToInteger: true,
      };
    default:
      return { label: key };
  }
};
